import React, { FC, useCallback, useEffect, useState } from 'react';
import { TFunction } from 'react-i18next';
import i18next from 'i18next';
import {
    getAdminTokenFromApi,
    getOtpUrlFromApi,
} from '../../authorization/getAuthFromApi';
import { deleteFormFromDB } from '../../apiCalls/deleteForm';
import { Button } from '@mercell/button-react';
import {
    DataFormat16,
    Edit16,
    TrashCan16,
    Warning32,
    Checkmark32,
    Catalog32,
    Save32,
    DataShare32,
    CloudDownload32,
    Upload16,
    Html16,
    Pdf16,
    Locked16,
    Unlocked16,
    TreeView32,
    Renew24,
    Identification32,
} from '@carbon/icons-react';
import { Dropdown } from '@mercell/dropdown-react';
import { SelectOption } from '../../types/generated/selectOption';
import ConfirmationModal from '../../components/ConfirmationModal';
import { saveFormStatus } from '../../apiCalls/saveFormStatus';
import { upgradeForm } from '../../apiCalls/upgradeForm';
import { UserRole } from '../../types/generated/userRightsResult';
import { Tabs, Tab } from '@mercell/tabs-react';
import BulkOperationsTab from './BulkOperationsTab';
import { DropdownOption } from '../../types/generated/dropdownOption';
import { formOptions } from '../../types/enums/formOptions';
import { compareForms } from '../../apiCalls/compareForms';
import { publishForm } from '../../apiCalls/publishForm';
import UserCustomization from './UserCustomization';
import cx from 'classnames';
import { FormListItem, TenderListItem } from '../../types/tenderList';
import { debounce } from 'lodash';
import { translateForm } from '../../apiCalls/translateForm';
import { lockUnlockForm } from '../../apiCalls/lockUnlockForm';
import { KeyedMutator } from 'swr';
import { FormStatusResult } from '../../types/generated/formStatusResult';
import { fetchTenders } from '../../apiCalls/fetchTenders';
import { convertLanguageToCode } from '../../shared/utilities/languageToLocaleConvert';
import { previewPdf } from '../../apiCalls/previewPdf';
import { useAppSelector } from '../../redux/hooks';
import useFetchUserRights from '../../hooks/useFetchUserRights';
import { Input, InputContainer, InputLeftElement, InputRightElement } from '@mercell/input-react';

const singleFormTab = 'single-form';
const bulkOperationsTab = 'bulk-operations';

interface SelectFormProps {
    t: TFunction;
    onChangeTender: (newValue: TenderListItem | null) => void;
    onChangeStatus: (newValue: SelectOption | null) => void;
    onChangeForm: (newValue: FormListItem | null) => void;
    platformName?: string;
    formUserRights: number;
    tender: TenderListItem | null;
    status: SelectOption | null;
    form: FormListItem | null;
    platformNames: string[];
    onChangePlatformName: (e: any) => void;
    onChangeFormUserRights: (e: any) => void;
    currentStatus?: string;
    statuses?: DropdownOption[];
    mutateFormStatuses?: KeyedMutator<FormStatusResult>;
}

const SelectForm: FC<SelectFormProps> = ({
    t,
    onChangeTender,
    onChangeStatus,
    onChangeForm,
    platformName,
    formUserRights,
    tender,
    status,
    form,
    platformNames,
    onChangePlatformName,
    onChangeFormUserRights,
    currentStatus,
    statuses,
    mutateFormStatuses,
}) => {
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const [isPublishModalVisible, setIsPublishModalVisible] =
        useState<boolean>(false);
    const [selectKey, setSelectKey] = useState<any>(undefined);
    const [tenderOptions, setTenderOptions] = useState<any>();
    const [refetchTenders, setRefetchTenders] = useState<boolean>();
    const [isRefetchingTenders, setIsRefetchingTenders] = useState<boolean>();
    const { language } = i18next;
    const [organizationId, setOrganizationId] = useState<string>('');
    const [activeTab, setActiveTab] = useState<string>(singleFormTab);

    const reduxUserId = useAppSelector((state) => state.store.userId);
    const reduxRole = useAppSelector((state) => state.store.role);

    useEffect(() => {
        const fetchData = async () => {
            const data: any = await fetchTenders(
                t,
                platformName,
                false,
                undefined
            );
            const options = Object.keys(data).map((tenderListItem) => ({
                ...data[tenderListItem],
                value: tenderListItem,
                label: data[tenderListItem].name,
            }));
            return options;
        };
        if (refetchTenders != null) {
            setIsRefetchingTenders(true);
        }
        fetchData().then((res) => {
            setTenderOptions(res);
            setIsRefetchingTenders(false);
        });
    }, [platformName, t, refetchTenders]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadOptionsDebounced = useCallback(
        debounce((inputValue: string, callback: (options: any) => void) => {
            fetchTenders(t, platformName, false, inputValue).then((data) => {
                const options = Object.keys(data).map((tenderListItem) => ({
                    ...data[tenderListItem],
                    value: tenderListItem,
                    label: data[tenderListItem].name,
                }));
                callback(options);
            });
        }, 1000),
        [t, platformName]
    );

    const isUKNoticeSubType = (formItem: FormListItem | null) => typeof formItem?.noticeSubtype === "string" && formItem?.noticeSubtype.startsWith("UK")

    const generateAdminToken = (forCopy = false) =>
        getAdminTokenFromApi({
            name: platformName,
            userId: reduxUserId,
            formId: forCopy ? undefined : form?.value,
            tenderId: tender?.value,
            formUserRights: forCopy ? 2 : formUserRights,
            role: reduxRole,
        });

    const deleteForm = async () => {
        await generateAdminToken();
        if (form?.value) {
            await deleteFormFromDB(form.value, {
                onPendingText: t('toast-content:ToastDeleteFormPending'),
                onSuccessText: t('toast-content:ToastDeleteFormSuccess'),
            }).then(() => {
                const formToDelete = tender?.forms.find(
                    (deletedForm) => deletedForm.value === form.value
                );
                if (formToDelete && tender) {
                    const indexToRemove = tender?.forms.indexOf(formToDelete);
                    if (indexToRemove !== -1) {
                        tender?.forms.splice(indexToRemove, 1);
                        onChangeForm(null);
                    }
                }
                if (tender && tender.forms.length < 1) {
                    onChangeTender(null);
                    setSelectKey(formToDelete?.value);
                }
            });
        }
    };

    const onSaveFormStatus = async () => {
        if (form?.value && status?.value?.toString()) {
            saveFormStatus(form.value, status.value.toString(), {
                onPendingText: t('toast-content:ToastSaving'),
                onSuccessText: t('toast-content:ToastSaveNoticeStatusSuccess'),
            }).then(() => {
                if (mutateFormStatuses) {
                    mutateFormStatuses();
                }
            });
        }
    };

    const onFormUpgrade = async () => {
        if (form?.value) {
            upgradeForm(form.value, {
                onPendingText: t('toast-content:ToastSaving'),
                onSuccessText: t('toast-content:ToastFormUpgradeSuccess'),
            }).then(() => {
                // empty
            });
        }
    };

    const onGoToForm = async (option: formOptions) => {
        await generateAdminToken();
        if (form?.value) {
            const otpUrl = await getOtpUrlFromApi(
                form.value,
                formUserRights,
                option
            );

            // if using https://dev.mercellforms.mercell.com/ for backend uncomment this and bellow replace "otpUrl" with "url"
            // const url = otpUrl.replace(
            //     'https://dev.mercellforms.mercell.com/',
            //     'http://localhost:3000/'
            // );

            window.open(otpUrl ?? '/login', '_blank')?.focus();
        }
    };

    const onAuditLog = async () => {
        await generateAdminToken();
        window.open(
            form?.value ? `form/auditlog/${form.value}` : '/login',
            '_blank'
        );
    };

    const onPublicationStatuses = async () => {
        await generateAdminToken();
        window.open(
            form?.value ? `form/publicationtask/${form.value}` : '/login',
            '_blank'
        );
    };

    const onDocumentFamily = async () => {
        await generateAdminToken();
        window.open(
            form?.value ? `form/family/${form.value}` : '/login',
            '_blank'
        );
    };

    const onPreviewHtml = async () => {
        await generateAdminToken();
        window.open(
            form?.value ? `form/previewhtml/${form.value}` : '/login',
            '_blank'
        );
    };

    const onPreviewPdf = async () => {
        await generateAdminToken();

        const languageCode = convertLanguageToCode(language);
        previewPdf(form?.value, languageCode, {
            onPendingText: t('toast-content:ToastDownloading'),
            onSuccessText: t('toast-content:ToastDownloadingSuccess'),
        });
    };

    const onDeleteForm = async () => {
        await generateAdminToken();
        setIsModalVisible(true);
    };

    const onConfirmDelete = () => {
        deleteForm();
        setIsModalVisible(false);
    };

    const onLockForm = async () => {
        await getAdminTokenFromApi({
            name: platformName,
            userId: reduxUserId,
            formId: form?.value,
            tenderId: tender?.value,
            formUserRights: 2,
            role: reduxRole,
        });
        lockUnlockForm(form?.value, 'true', {
            onPendingText: t('toast-content:ToastLockFormPending'),
            onSuccessText: t('toast-content:ToastLockFormSuccess'),
        });
    };

    const onUnlockForm = async () => {
        await getAdminTokenFromApi({
            name: platformName,
            userId: reduxUserId,
            formId: form?.value,
            tenderId: tender?.value,
            formUserRights: 2,
            role: reduxRole,
        });
        lockUnlockForm(form?.value, 'false', {
            onPendingText: t('toast-content:ToastUnlockFormPending'),
            onSuccessText: t('toast-content:ToastUnlockFormSuccess'),
        });
    };

    const onPublishForm = async () => {
        await getAdminTokenFromApi({
            name: platformName,
            userId: reduxUserId,
            formId: form?.value,
            tenderId: tender?.value,
            formUserRights: 2,
            role: reduxRole,
        });
        setIsPublishModalVisible(true);
    };

    const onTranslateForm = async () => {
        await getAdminTokenFromApi({
            name: platformName,
            userId: reduxUserId,
            formId: form?.value,
            tenderId: tender?.value,
            formUserRights: 2,
            role: reduxRole,
        });
        translateForm(
            form?.value,
            {
                onPendingText: t('toast-content:ToastTranslateFormPending'),
                onSuccessText: t('toast-content:ToastTranslateFormSuccess'),
            },
            undefined,
            false
        );
    };

    const onConfirmPublish = async () => {
        if (form?.value) {
            setIsPublishModalVisible(false);
            publishForm(form.value, organizationId, {
                onSuccessText: t('toast-content:ToastPublishFormSuccess'),
                onPendingText: t('toast-content:ToastPublishFormPending'),
            });
        }
    };

    const onCompare = async () => {
        if (form?.value) {
            compareForms(form.value);
        }
    };

    const { UserRights } = useFetchUserRights();

    const canRunBulkFormActions =
        UserRights?.userRole === UserRole.SuperAdmin ||
        UserRights?.statusChangeEnabledLevel === 2;

    const showSelectFormTab =
        !canRunBulkFormActions || activeTab === singleFormTab;

    const tabPanelContentClassName = 'w-full';

    const singleFormGridClassName = cx(
        'grid w-full',
        canRunBulkFormActions ? 'mt-4 grid-cols-1' : 'mt-10 grid-cols-2'
    );

    return (
        <>
            <h1>{t('title')}</h1>
            <UserCustomization
                t={t}
                platformNames={platformNames ?? []}
                onChangePlatformName={onChangePlatformName}
                onChangeFormUserRights={onChangeFormUserRights}
                formUserRights={formUserRights}
            />
            {isModalVisible && (
                <ConfirmationModal
                    cancelText={t('form-content:LabelCancel')}
                    confirmText={t('form-content:LabelDelete')}
                    onConfirm={onConfirmDelete}
                    closeModal={() => setIsModalVisible(false)}
                    isModalVisible={isModalVisible}
                    title={t('form-content:ConfirmDelete')}
                >
                    <p className="text-center">
                        {t('form-content:DeleteConfirmation')}
                    </p>
                </ConfirmationModal>
            )}

            {isPublishModalVisible && (
                <ConfirmationModal
                    text={t('form-content:PublishConfirmation')}
                    cancelText={t('form-content:LabelCancel')}
                    confirmText={t('form-content:PublishForm')}
                    onConfirm={onConfirmPublish}
                    closeModal={() => setIsPublishModalVisible(false)}
                    isModalVisible={isPublishModalVisible}
                >
                    <Warning32
                        style={{
                            width: 150,
                            height: 150,
                            margin: '30px auto',
                            color: 'orange',
                        }}
                    />
                </ConfirmationModal>
            )}
            {canRunBulkFormActions && (
                <div className="mt-8 w-full" data-test="forminator-tabs">
                    <Tabs activeKey={activeTab} onTabChange={setActiveTab}>
                        <Tab
                            tabKey={singleFormTab}
                            title={t('form-content:TabSelectForm')}
                        />
                        <Tab
                            tabKey={bulkOperationsTab}
                            title={t('form-content:TabBulkOperations')}
                        />
                    </Tabs>
                </div>
            )}
            <div
                className={cx(
                    canRunBulkFormActions && 'mt-6 min-h-[28rem] w-full'
                )}
                data-test={
                    canRunBulkFormActions
                        ? 'forminator-tab-panels'
                        : undefined
                }
            >
                <div
                    className={cx(
                        canRunBulkFormActions &&
                            !showSelectFormTab &&
                            'hidden',
                        tabPanelContentClassName
                    )}
                    data-test="select-form-section"
                >
                    <div className={singleFormGridClassName}>
                        <div className="col-span-2">
                    <div className="flex items-center">
                        <label htmlFor="tenderId">
                            {t('form-content:LabelTender')}
                        </label>
                        {!isRefetchingTenders ? (
                            <Renew24
                                onClick={() => {
                                    setRefetchTenders(
                                        (refetchTendersVal) =>
                                            !refetchTendersVal
                                    );
                                }}
                                className="ml-auto mr-2"
                            />
                        ) : (
                            <span className="animate-spin ml-auto mr-2">
                                <Renew24 className="-scale-x-[1]" />
                            </span>
                        )}
                    </div>
                    <Dropdown
                        key={selectKey}
                        id="tenderId"
                        className="mt-4"
                        name="tenderId"
                        menuPosition="fixed"
                        isClearable
                        value={tender ?? null}
                        styles={{
                            loadingMessage: (styles) => {
                                styles.color = 'white';
                                return styles;
                            },
                            noOptionsMessage: (styles) => {
                                styles.color = 'white';
                                return styles;
                            },
                            menuList: (styles, state) => {
                                if (state.options.length < 1) {
                                    styles.background = 'var(--minsk)';
                                    styles.color = 'white';
                                    styles.borderRadius = '0.5rem';
                                }
                                return styles;
                            },
                            loadingIndicator: (styles) => {
                                styles.color = 'var(--minsk)';
                                return styles;
                            },
                        }}
                        isAsync
                        defaultOptions={tenderOptions}
                        cacheOptions
                        onChange={(tenderListItem) => {
                            onChangeTender(tenderListItem as TenderListItem);
                        }}
                        loadOptions={loadOptionsDebounced}
                        placeholder={t('form-content:ChooseTender')}
                    />

                    <div />
                </div>

                <div
                    className={cx(
                        'mt-10 col-span-2',
                        !canRunBulkFormActions &&
                            (form?.value ? 'lg:col-span-1' : 'lg:col-span-2')
                    )}
                >
                    {tender && tender.forms.length > 0 && (
                        <>
                            <label htmlFor="formId">
                                {t('form-content:LabelForm')}
                            </label>
                            <Dropdown
                                id="formId"
                                name="formId"
                                className="mt-4"
                                menuPosition="fixed"
                                value={form}
                                isClearable
                                onChange={(tenderForm) => {
                                    onChangeForm(
                                        tenderForm as FormListItem | null
                                    );
                                }}
                                options={tender.forms.map((tenderForm) => ({
                                    ...tenderForm,
                                    value: tenderForm.value,
                                    label: tenderForm.name,
                                }))}
                                placeholder={t('form-content:ChooseForm')}
                            />
                        </>
                    )}
                </div>

                {form?.value && tender && (
                    <>
                        <div
                            className={cx(
                                'flex items-center mt-20 col-span-2 gap-x-2 whitespace-nowrap',
                                !canRunBulkFormActions &&
                                    'lg:ml-4 lg:col-span-1'
                            )}
                        >
                            <Button
                                iconSettings={{ Icon: DataFormat16 }}
                                scheme="primary"
                                onClick={() => onGoToForm(formOptions.form)}
                                title={t('OpenForm')}
                                data-test="open-form-button"
                            >
                                {t('OpenForm')}
                            </Button>
                            <Button
                                iconSettings={{ Icon: Edit16 }}
                                scheme="primary"
                                onClick={() => onGoToForm(formOptions.json)}
                                title={t('EditFormJson')}
                                data-test="edit-form-button"
                            >
                                {t('EditFormJson')}
                            </Button>

                            {!!UserRights?.isAdmin && (
                                <Button
                                    iconSettings={{ Icon: TrashCan16 }}
                                    scheme="secondary"
                                    onClick={onDeleteForm}
                                    title={t('LabelDelete')}
                                    data-test="delete-form-button"
                                >
                                    {t('LabelDelete')}
                                </Button>
                            )}
                        </div>
                        <div className="flex flex-wrap mt-5 lg:mt-10 col-span-2 gap-5">
                            <Button
                                iconSettings={{ Icon: CloudDownload32 }}
                                scheme="primary"
                                onClick={() =>
                                    onGoToForm(formOptions.downloadxml)
                                }
                                title={t('DownloadXml')}
                                data-test="downloadxml-form-button"
                            >
                                {t('DownloadXml')}
                            </Button>
                            <Button
                                iconSettings={{ Icon: Checkmark32 }}
                                scheme="primary"
                                onClick={() =>
                                    onGoToForm(formOptions.validationSchematron)
                                }
                                title={t('ValidateFormSchematron')}
                                data-test="validate-form-schematron-button"
                            >
                                {t('ValidateFormSchematron')}
                            </Button>
                            <Button
                                iconSettings={{ Icon: Checkmark32 }}
                                scheme="primary"
                                onClick={() =>
                                    onGoToForm(formOptions.validationQuick)
                                }
                                title={t('ValidateFormQuick')}
                                data-test="validate-form-quick-button"
                            >
                                {t('ValidateFormQuick')}
                            </Button>
                            <Button
                                iconSettings={{ Icon: Checkmark32 }}
                                scheme="primary"
                                onClick={() =>
                                    onGoToForm(formOptions.validationFull)
                                }
                                title={t('ValidateFormFull')}
                                data-test="validate-form-full-button"
                            >
                                {t('ValidateFormFull')}
                            </Button>
                        </div>
                        <div className="flex flex-wrap mt-5 lg:mt-10 col-span-2 gap-5">
                            <Button
                                iconSettings={{ Icon: Catalog32 }}
                                scheme="primary"
                                onClick={() => onAuditLog()}
                                data-test="audit-log-button"
                            >
                                {t('AuditLog')}
                            </Button>
                            <Button
                                iconSettings={{ Icon: Html16 }}
                                scheme="primary"
                                onClick={() => onPreviewHtml()}
                                data-test="preview-html-button"
                            >
                                {t('PreviewHtml')}
                            </Button>
                            <Button
                                iconSettings={{ Icon: Pdf16 }}
                                scheme="primary"
                                onClick={() => onPreviewPdf()}
                                data-test="preview-pdf-button"
                            >
                                {t('PreviewPdf')}
                            </Button>
                            {currentStatus === 'ReadyForPublication' &&
                              !!UserRights?.isAdmin && isUKNoticeSubType(form) && (

                                <InputContainer className="w-[500px] rounded-default min-w-[50%] ">
                                    <InputLeftElement className="pr-2">
                                        <Identification32 />
                                    </InputLeftElement>
                                    <Input
                                        placeholder={t('OrganizationID')}
                                        value={organizationId}
                                        onChange={(e) =>
                                            setOrganizationId(e.target.value)
                                        }
                                        className="focus:placeholder:invisible"
                                    />
                                    <InputRightElement className="pl-4 border-l-2 border-alto " />
                                </InputContainer>

                              )}
                            {currentStatus === 'ReadyForPublication' &&
                                !!UserRights?.isAdmin && (
                                    <Button
                                        iconSettings={{
                                            Icon: Upload16,
                                        }}
                                        scheme="primary"
                                        onClick={() => onPublishForm()}
                                        title={t('PublishForm')}
                                        data-test="publish-form-button"
                                    >
                                        {t('PublishForm')}
                                    </Button>
                                )}
                            {currentStatus === 'ReadyForTranslation' && (
                                <Button
                                    iconSettings={{
                                        Icon: Upload16,
                                    }}
                                    scheme="primary"
                                    onClick={() => onTranslateForm()}
                                    title={t('TranslateForm')}
                                    data-test="translate-form-button"
                                >
                                    {t('TranslateForm')}
                                </Button>
                            )}
                            {form?.label?.includes('Change') && (
                                <Button
                                    iconSettings={{ Icon: DataShare32 }}
                                    scheme="primary"
                                    onClick={onCompare}
                                    title={t('Compare')}
                                    data-test="compare-forms-button"
                                >
                                    {t('Compare')}
                                </Button>
                            )}
                        </div>

                        {!!UserRights?.isAdmin && (
                            <div className="flex flex-wrap mt-5 lg:mt-8 col-span-2 gap-5">
                                <Button
                                    iconSettings={{ Icon: Locked16 }}
                                    scheme="tertiary"
                                    onClick={() => onLockForm()}
                                    title={t('LockForm')}
                                >
                                    {t('LockForm')}
                                </Button>
                                <Button
                                    iconSettings={{ Icon: Unlocked16 }}
                                    scheme="tertiary"
                                    onClick={() => onUnlockForm()}
                                    title={t('UnlockForm')}
                                >
                                    {t('UnlockForm')}
                                </Button>
                            </div>
                        )}
                        <div className="flex flex-wrap mt-5 lg:mt-8 col-span-2 gap-5">
                            <Button
                                iconSettings={{ Icon: TreeView32 }}
                                scheme="tertiary"
                                onClick={() => onDocumentFamily()}
                                data-test="document-family-button"
                            >
                                {t('DocumentFamily')}
                            </Button>
                            <Button
                                iconSettings={{ Icon: Catalog32 }}
                                scheme="tertiary"
                                onClick={() => onPublicationStatuses()}
                                data-test="publication-statuses-button"
                            >
                                {t('PublicationData')}
                            </Button>
                        </div>
                    </>
                )}
                    </div>
                    {statuses && tender?.value && form?.value && (
                        <>
                            <div className="flex w-full mt-4 items-center gap-5 whitespace-nowrap">
                                <div className="w-4/6">
                                    <label htmlFor="status">
                                        {t(`LabelFormStatus`)}
                                    </label>
                                    <Dropdown
                                        id="status"
                                        name="status"
                                        className="mt-4"
                                        menuPosition="fixed"
                                        value={status}
                                        menuPlacement="top"
                                        onChange={(newValue) => {
                                            onChangeStatus(
                                                newValue as SelectOption | null
                                            );
                                        }}
                                        options={statuses.map(
                                            (option: DropdownOption) => ({
                                                value: option.value,
                                                label: t(
                                                    `list.audit-log-status:${option.name}`
                                                ),
                                            })
                                        )}
                                        placeholder="Select status"
                                    />
                                </div>

                                {!!UserRights?.isAdmin && (
                                    <Button
                                        iconSettings={{ Icon: Save32 }}
                                        scheme="primary"
                                        className="mt-10 w-fit"
                                        onClick={() => onSaveFormStatus()}
                                        title={t('LabelSaveStatus')}
                                        data-test="status-button"
                                    >
                                        {t('LabelSaveStatus')}
                                    </Button>
                                )}
                            </div>
                            <div className="flex w-full mt-4 items-center gap-5 whitespace-nowrap">
                                <div className="w-4/6">
                                    {!!UserRights?.isAdmin && (
                                        <Button
                                            iconSettings={{ Icon: Save32 }}
                                            scheme="secondary"
                                            className="mt-10 w-fit"
                                            onClick={() => onFormUpgrade()}
                                            title={t('LabelUpgradeForm')}
                                            data-test="form-upgrade-button"
                                        >
                                            {t('LabelUpgradeForm')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                {canRunBulkFormActions && (
                    <div
                        className={cx(
                            tabPanelContentClassName,
                            showSelectFormTab && 'hidden'
                        )}
                    >
                        <BulkOperationsTab
                            t={t}
                            statuses={statuses}
                            mutateFormStatuses={mutateFormStatuses}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default SelectForm;
